
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Line, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';

interface IssuanceChartProps {
  dateRange: { from: Date | undefined; to: Date | undefined };
}

export function IssuanceChart({ dateRange }: IssuanceChartProps) {
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Define date range for the query
  const endDate = dateRange.to || new Date();
  const startDate = dateRange.from || subMonths(endDate, 5);
  
  const { data: certificates, isLoading } = useQuery({
    queryKey: ['certificatesForChart', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificates')
        .select('id, issued_at')
        .gte('issued_at', startDate.toISOString())
        .lte('issued_at', endDate.toISOString());
      
      if (error) throw error;
      return data;
    },
  });
  
  useEffect(() => {
    if (!certificates) return;
    
    // Generate all months in the range
    const monthsInRange = eachMonthOfInterval({
      start: startOfMonth(startDate),
      end: endOfMonth(endDate)
    });
    
    // Initialize data structure with all months
    const monthData = monthsInRange.map(date => {
      const monthStr = format(date, 'MMM yyyy');
      return {
        month: monthStr,
        count: 0
      };
    });
    
    // Count certificates for each month
    certificates.forEach(cert => {
      const certDate = new Date(cert.issued_at);
      const monthStr = format(certDate, 'MMM yyyy');
      const monthIndex = monthData.findIndex(item => item.month === monthStr);
      
      if (monthIndex !== -1) {
        monthData[monthIndex].count += 1;
      }
    });
    
    setChartData(monthData);
  }, [certificates, startDate, endDate]);
  
  if (isLoading) {
    return <div>Loading chart data...</div>;
  }
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="count" 
          name="Certificates Issued" 
          stroke="#00dd00" 
          activeDot={{ r: 8 }} 
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
